import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * SplashScreen – Premium industrial-themed full-screen loader.
 * Features: dark industrial gradient, animated steam particles,
 * glowing logo reveal, and a sleek progress bar.
 */

/* ── Animated steam particle ── */
const SteamParticle = ({ delay, x, size }) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      left: `${x}%`,
      bottom: '10%',
      background: 'radial-gradient(circle, rgba(249,115,22,0.3) 0%, rgba(249,115,22,0) 70%)',
      filter: 'blur(2px)',
    }}
    initial={{ opacity: 0, y: 0, scale: 0.5 }}
    animate={{
      opacity: [0, 0.7, 0.4, 0],
      y: [0, -120, -260, -400],
      scale: [0.5, 1.2, 0.8, 0.3],
      x: [0, Math.random() * 40 - 20, Math.random() * 60 - 30],
    }}
    transition={{
      duration: 3 + Math.random() * 2,
      delay,
      repeat: Infinity,
      ease: 'easeOut',
    }}
  />
);

const SplashScreen = () => {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 2400);

    // Animate progress bar
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 15 + 5;
      });
    }, 150);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
          style={{
            zIndex: 99999,
            background: 'linear-gradient(145deg, #04070d 0%, #0a1020 30%, #0d0f18 60%, #080c16 100%)',
          }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
          {/* ── Industrial grid overlay ── */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(249,115,22,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.5) 1px, transparent 1px)',
              backgroundSize: '80px 80px',
            }}
          />

          {/* ── Radial glow behind logo ── */}
          <motion.div
            className="absolute"
            style={{
              width: 400,
              height: 400,
              background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, rgba(249,115,22,0.03) 40%, transparent 70%)',
            }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* ── Steam particles ── */}
          {[...Array(8)].map((_, i) => (
            <SteamParticle
              key={i}
              delay={i * 0.35}
              x={30 + Math.random() * 40}
              size={8 + Math.random() * 16}
            />
          ))}

          {/* ── Logo ── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
            className="relative z-10 flex flex-col items-center"
          >
            {/* Logo image with glow ring */}
            <div className="relative mb-6">
              <motion.div
                className="absolute -inset-3 rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(249,115,22,0.4), rgba(251,146,60,0.1), rgba(249,115,22,0.3))',
                  filter: 'blur(12px)',
                }}
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <img
                src="/steamx-logo.png"
                alt="SteamX Systems"
                className="relative w-20 h-20 rounded-2xl object-cover shadow-2xl"
                style={{ boxShadow: '0 0 40px rgba(249,115,22,0.25)' }}
              />
            </div>

            {/* Brand name */}
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-white font-extrabold text-3xl tracking-tight">Steam</span>
              <span className="text-orange-400 font-extrabold text-3xl tracking-tight">X</span>
            </div>
            <span className="text-slate-500 text-xs font-medium tracking-[0.3em] uppercase mb-8">
              Systems
            </span>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="text-slate-500 text-sm font-medium italic mb-10"
            >
              "Extra degree for the change"
            </motion.p>

            {/* Progress bar */}
            <div className="w-48 h-[2px] bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{
                  background: 'linear-gradient(90deg, #f97316, #fb923c, #f97316)',
                  width: `${Math.min(progress, 100)}%`,
                }}
                transition={{ duration: 0.15, ease: 'linear' }}
              />
            </div>
          </motion.div>

          {/* ── Bottom industrial accent ── */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
